import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { payrollService } from '../../../services/payrollService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayrollProcessing = ({ staffMembers, selectedPeriod, onRefresh }) => {
  const { user, currentTenant } = useAuth();
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [calculatedPayroll, setCalculatedPayroll] = useState(null);
  const [manualAdjustments, setManualAdjustments] = useState({
    bonusAmount: 0,
    commissionAmount: 0,
    additionalDeductions: []
  });
  const [showManualEntry, setShowManualEntry] = useState(false);

  const handleCalculatePayroll = async (staffMember) => {
    setSelectedStaff(staffMember);
    setProcessing(true);
    try {
      // Calculate from attendance
      const payrollData = await payrollService?.calculatePayrollFromAttendance(
        currentTenant?.id,
        staffMember?.user_id,
        selectedPeriod?.start,
        selectedPeriod?.end
      );

      if (!payrollData) {
        throw new Error('Unable to calculate payroll. Please check wage rates and attendance data.');
      }

      // Calculate tax deductions
      const grossPay = parseFloat(payrollData?.gross_pay || 0) + 
                       parseFloat(manualAdjustments?.bonusAmount || 0) + 
                       parseFloat(manualAdjustments?.commissionAmount || 0);
      
      const taxDeductions = await payrollService?.calculateTaxDeductions(
        currentTenant?.id,
        grossPay
      );

      setCalculatedPayroll({
        ...payrollData,
        bonusAmount: manualAdjustments?.bonusAmount,
        commissionAmount: manualAdjustments?.commissionAmount,
        taxDeductions,
        grossPay,
        totalDeductions: taxDeductions?.reduce((sum, d) => sum + parseFloat(d?.amount || 0), 0) +
                        manualAdjustments?.additionalDeductions?.reduce((sum, d) => sum + parseFloat(d?.amount || 0), 0),
        netPay: grossPay - (taxDeductions?.reduce((sum, d) => sum + parseFloat(d?.amount || 0), 0) +
                manualAdjustments?.additionalDeductions?.reduce((sum, d) => sum + parseFloat(d?.amount || 0), 0))
      });
    } catch (error) {
      console.error('Error calculating payroll:', error);
      alert(error?.message || 'Error calculating payroll');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessPayroll = async () => {
    if (!calculatedPayroll || !selectedStaff) return;

    setProcessing(true);
    try {
      // Create payroll record
      const payrollRecord = await payrollService?.createPayrollRecord({
        tenant_id: currentTenant?.id,
        staff_id: selectedStaff?.user_id,
        pay_period_start: selectedPeriod?.start,
        pay_period_end: selectedPeriod?.end,
        regular_hours: calculatedPayroll?.regular_hours,
        overtime_hours: calculatedPayroll?.overtime_hours,
        regular_pay: calculatedPayroll?.regular_pay,
        overtime_pay: calculatedPayroll?.overtime_pay,
        bonus_amount: calculatedPayroll?.bonusAmount,
        commission_amount: calculatedPayroll?.commissionAmount,
        gross_pay: calculatedPayroll?.grossPay,
        total_deductions: calculatedPayroll?.totalDeductions,
        net_pay: calculatedPayroll?.netPay,
        payment_status: 'pending',
        created_by: user?.id
      });

      // Add deductions
      const allDeductions = [
        ...calculatedPayroll?.taxDeductions?.map(d => ({
          deduction_type: d?.tax_category?.replace('_', ' ')?.toUpperCase(),
          deduction_category: d?.tax_category,
          amount: d?.amount,
          is_percentage: false
        })),
        ...manualAdjustments?.additionalDeductions
      ];

      if (allDeductions?.length > 0) {
        await payrollService?.addPayrollDeductions(payrollRecord?.id, allDeductions);
      }

      alert('Payroll processed successfully!');
      setSelectedStaff(null);
      setCalculatedPayroll(null);
      setManualAdjustments({ bonusAmount: 0, commissionAmount: 0, additionalDeductions: [] });
      onRefresh();
    } catch (error) {
      console.error('Error processing payroll:', error);
      alert('Error processing payroll');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddDeduction = () => {
    setManualAdjustments({
      ...manualAdjustments,
      additionalDeductions: [
        ...manualAdjustments?.additionalDeductions,
        { deduction_type: '', deduction_category: 'other', amount: 0, description: '' }
      ]
    });
  };

  const handleUpdateDeduction = (index, field, value) => {
    const updated = [...manualAdjustments?.additionalDeductions];
    updated[index][field] = value;
    setManualAdjustments({ ...manualAdjustments, additionalDeductions: updated });
  };

  const handleRemoveDeduction = (index) => {
    const updated = manualAdjustments?.additionalDeductions?.filter((_, i) => i !== index);
    setManualAdjustments({ ...manualAdjustments, additionalDeductions: updated });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Manual Payroll Processing</h2>
        <Button
          onClick={() => setShowManualEntry(!showManualEntry)}
          variant="outline"
        >
          <Icon name={showManualEntry ? "EyeOff" : "Eye"} size={18} className="mr-2" />
          {showManualEntry ? 'Hide' : 'Show'} Manual Entry
        </Button>
      </div>

      {/* Staff Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {staffMembers?.map((staff) => (
          <div
            key={staff?.id}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedStaff?.id === staff?.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onClick={() => !processing && handleCalculatePayroll(staff)}
          >
            <div className="flex items-center gap-3 mb-3">
              {staff?.user_profiles?.avatar_url ? (
                <img
                  src={staff?.user_profiles?.avatar_url}
                  alt={staff?.user_profiles?.full_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
              )}
              <div>
                <h3 className="font-medium text-foreground">{staff?.user_profiles?.full_name}</h3>
                <p className="text-xs text-muted-foreground capitalize">{staff?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {staff?.staff_wage_rates?.[0] ? (
                <span className="text-green-600">✓ Wage rate configured</span>
              ) : (
                <span className="text-yellow-600">⚠ No wage rate</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Manual Adjustments */}
      {showManualEntry && selectedStaff && (
        <div className="border border-border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-foreground">Manual Adjustments</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Bonus Amount</label>
              <input
                type="number"
                step="0.01"
                value={manualAdjustments?.bonusAmount}
                onChange={(e) => setManualAdjustments({ ...manualAdjustments, bonusAmount: parseFloat(e?.target?.value) || 0 })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Commission Amount</label>
              <input
                type="number"
                step="0.01"
                value={manualAdjustments?.commissionAmount}
                onChange={(e) => setManualAdjustments({ ...manualAdjustments, commissionAmount: parseFloat(e?.target?.value) || 0 })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-foreground">Additional Deductions</label>
              <Button onClick={handleAddDeduction} variant="outline" size="sm">
                <Icon name="Plus" size={16} className="mr-1" />
                Add Deduction
              </Button>
            </div>
            {manualAdjustments?.additionalDeductions?.map((deduction, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Deduction type"
                  value={deduction?.deduction_type}
                  onChange={(e) => handleUpdateDeduction(index, 'deduction_type', e?.target?.value)}
                  className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
                <select
                  value={deduction?.deduction_category}
                  onChange={(e) => handleUpdateDeduction(index, 'deduction_category', e?.target?.value)}
                  className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                >
                  <option value="health_insurance">Health Insurance</option>
                  <option value="retirement">Retirement</option>
                  <option value="garnishment">Garnishment</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  value={deduction?.amount}
                  onChange={(e) => handleUpdateDeduction(index, 'amount', parseFloat(e?.target?.value) || 0)}
                  className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
                <Button
                  onClick={() => handleRemoveDeduction(index)}
                  variant="outline"
                  className="text-red-600"
                >
                  <Icon name="Trash2" size={16} />
                </Button>
              </div>
            ))}
          </div>

          <Button onClick={() => handleCalculatePayroll(selectedStaff)} className="w-full">
            <Icon name="Calculator" size={18} className="mr-2" />
            Recalculate with Adjustments
          </Button>
        </div>
      )}

      {/* Calculated Payroll Preview */}
      {calculatedPayroll && selectedStaff && (
        <div className="border border-primary rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">
              Payroll Preview - {selectedStaff?.user_profiles?.full_name}
            </h3>
            <button
              onClick={() => {
                setSelectedStaff(null);
                setCalculatedPayroll(null);
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Regular Hours</p>
              <p className="text-lg font-semibold text-foreground">{calculatedPayroll?.regular_hours}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Overtime Hours</p>
              <p className="text-lg font-semibold text-foreground">{calculatedPayroll?.overtime_hours}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Regular Pay</p>
              <p className="text-lg font-semibold text-foreground">${parseFloat(calculatedPayroll?.regular_pay)?.toFixed(2)}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Overtime Pay</p>
              <p className="text-lg font-semibold text-foreground">${parseFloat(calculatedPayroll?.overtime_pay)?.toFixed(2)}</p>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base Pay</span>
                <span className="font-medium text-foreground">
                  ${(parseFloat(calculatedPayroll?.regular_pay) + parseFloat(calculatedPayroll?.overtime_pay))?.toFixed(2)}
                </span>
              </div>
              {calculatedPayroll?.bonusAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bonus</span>
                  <span className="font-medium text-green-600">+${parseFloat(calculatedPayroll?.bonusAmount)?.toFixed(2)}</span>
                </div>
              )}
              {calculatedPayroll?.commissionAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Commission</span>
                  <span className="font-medium text-green-600">+${parseFloat(calculatedPayroll?.commissionAmount)?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">Gross Pay</span>
                <span className="text-foreground">${parseFloat(calculatedPayroll?.grossPay)?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h4 className="font-medium text-foreground mb-3">Deductions</h4>
            <div className="space-y-2">
              {calculatedPayroll?.taxDeductions?.map((deduction, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-muted-foreground capitalize">{deduction?.tax_category?.replace('_', ' ')}</span>
                  <span className="text-red-600">-${parseFloat(deduction?.amount)?.toFixed(2)}</span>
                </div>
              ))}
              {manualAdjustments?.additionalDeductions?.map((deduction, index) => (
                <div key={`manual-${index}`} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{deduction?.deduction_type}</span>
                  <span className="text-red-600">-${parseFloat(deduction?.amount)?.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between font-semibold pt-2 border-t border-border">
                <span className="text-foreground">Total Deductions</span>
                <span className="text-red-600">-${parseFloat(calculatedPayroll?.totalDeductions)?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="border-t-2 border-primary pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-foreground">Net Pay</span>
              <span className="text-2xl font-bold text-primary">${parseFloat(calculatedPayroll?.netPay)?.toFixed(2)}</span>
            </div>
          </div>

          <Button
            onClick={handleProcessPayroll}
            disabled={processing}
            className="w-full"
          >
            {processing ? (
              <Icon name="Loader2" size={18} className="animate-spin mr-2" />
            ) : (
              <Icon name="Check" size={18} className="mr-2" />
            )}
            Process Payroll
          </Button>
        </div>
      )}

      {!selectedStaff && (
        <div className="text-center py-12">
          <Icon name="DollarSign" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Select an employee to process payroll</p>
        </div>
      )}
    </div>
  );
};

export default PayrollProcessing;